/**
 * The words a person who is no superadmin reads (ADR-0140, "Technical
 * detail is the superadmin's"): no citation of the rulebook, and no message
 * that carries the platform's internals. Pure, so `errors.ts` and the
 * components share one rule; `app/technical.tsx` holds the hooks.
 */
// "(ADR-0135)", "(ADR-0135 §2)", "(§18)", "(§15, §17)", "(Amendment 0008)",
// "(ADR-0139; migration 0112)", "(setup step prospecting.ecb-rates)" and the
// same without parentheses at the head of a phrase ("ADR-0123 · across …").
const CITATION = /\s*\((?:[^()]*?(?:ADR-\d{4}|§\s?\d+|[Aa]mendment \d{4}|migration \d{4}|setup step [a-z0-9_.-]+)[^()]*)\)/g;
const LEADING = /^(?:ADR-\d{4}|[Aa]mendment \d{4}|§\s?\d+)(?:\s*§\s?\d+)?\s*[·:—-]\s*/;
const BARE = /\s*(?:ADR-\d{4}(?:\s*§\s?\d+)?|[Aa]mendment \d{4}|§\s?\d+)(?=[\s,.;:)]|$)/g;

/**
 * A sentence without the rulebook's citations: what a person needs is what
 * happened and what to do, not where the rule is written.
 */
export function plainWords(text: string): string {
  // Text that cites nothing is returned as it came, spacing and all: a
  // fragment of a sentence (" — 3 tools") keeps its leading space.
  if (!/ADR-\d{4}|§\s?\d|[Aa]mendment \d{4}|migration \d{4}|setup step /.test(text)) return text;
  const lead = /^\s/.test(text) ? ' ' : '';
  const tail = /\s$/.test(text) ? ' ' : '';
  const plain = text.replace(CITATION, '').replace(LEADING, '').replace(BARE, '').replace(/\s{2,}/g, ' ').replace(/\s+([.,;:])/g, '$1').trim();
  return plain ? `${lead}${plain}${tail}` : '';
}

/**
 * Whether a message carries the platform's internals — a rule's citation, a
 * tool or scope id, a manifest, the gateway, the heartbeat, a command, an
 * environment variable. A person who is no superadmin is not shown such a
 * message; they are told who can move it.
 */
// The platform's own namespaces: a tool or a scope id starts with one of
// these. Anything else shaped like `name.tld` is a company's web address,
// which is the work, not the platform's workings.
const NAMESPACES = /^(?:outbound|fundraising|food_science|production|finance|executive|tech|platform|comms|research|integration|inference|workspace|google|hunter|apollo|customerio|cloudflare|website|telegram)$/;

/** Web addresses set aside: `maaemo.no` or `www.x.se` in a message is not an internal. */
function withoutDomains(text: string): string {
  return text
    .replace(/\b[\w.+-]+@[\w-]+\.[\w.]+\b/g, '')
    .replace(/https?:\/\/\S+/g, '')
    .replace(/\b([a-z0-9][a-z0-9-]*)((?:\.[a-z0-9-]+)*\.[a-z]{2,6})\b/g, (whole: string, first: string) => (NAMESPACES.test(first) ? whole : ''));
}

export function technical(text: string): boolean {
  return (
    /ADR-\d{4}|§\s?\d|[Aa]mendment \d{4}|migration \d{4}/.test(text) ||
    /\b[a-z][a-z_]*\.[a-z][a-z0-9_]*[a-z0-9]\b/.test(withoutDomains(text)) ||
    /\b(?:manifest|gateway|heartbeat|work-api|core-api|org:sync|pnpm|supervision token|scope|requiredScopes|re-sign|admit(?:s|ted)? the tool|the tool library|NORUM_[A-Z_]+|[A-Z]{3,}_[A-Z_]+|dept-runtime|Cloud Run|the ledger|ledger\/)\b/i.test(text)
  );
}

/** For anyone but a superadmin, a message with internals becomes who can move it. */
export const ASK_THE_SUPERADMIN = 'This needs a superadmin to switch something on. A superadmin can see the details.';
